import type { ReactNode } from 'react'
import { Chip, PageHead } from '@w7/ui'
import type { DataSourceKind } from './domain'
import { dataSourceLabel } from './dataSource'
import type { WorkspaceDescriptor, ModeDescriptor } from './routes'
import './book-management.css'

/**
 * The rail dropdown is the only way between the three pages of a workspace, so
 * the shell carries no tab strip: a second copy of the same six links reads as
 * navigation the operator has to keep in sync with the rail in their head.
 *
 * One heading, not two. "Book Management" over "Net Exposure" on separate lines
 * spent a row of a laptop screen restating what the rail already highlights, so
 * the workspace rides in the title itself and the page starts at the filters.
 *
 * Tickers own their PageHead (Members Name / Ip actions) — shell is just the
 * chrome wrapper so the table keeps every vertical pixel.
 */
export function WorkspaceShell({
  workspace,
  mode,
  source,
  children,
}: {
  workspace: WorkspaceDescriptor
  mode: ModeDescriptor
  source: DataSourceKind
  children: ReactNode
}) {
  const ticker = mode.surface === 'ticker'

  if (ticker) {
    return <div className="w7-bm w7-bm--ticker">{children}</div>
  }

  return (
    <div className="w7-bm">
      <PageHead
        title={
          <span className="w7-bm-title">
            <span className="w7-bm-title-workspace">{workspace.label}</span>
            <span className="w7-bm-title-sep" aria-hidden="true">
              /
            </span>
            {mode.label}
          </span>
        }
        subtitle={mode.description}
        actions={
          source === 'mock' ? (
            <Chip tone="warn" size="sm" data-testid="bm-data-source" role="status" aria-live="polite">
              {dataSourceLabel(source)}
            </Chip>
          ) : null
        }
      />

      <div className="w7-bm-body">{children}</div>
    </div>
  )
}
